import { useMemo } from 'react'

import { useGetMeetingQuery } from '../api'
import { useGetMeetingFromSupergraphQuery } from '../graphql_api'
import { mapNodeToMeeting } from '../utils'

/**
 * Fetches the primary Calendly meeting detail for the right rail: REST `getMeeting` by default,
 * or Supergraph `getMeetingById` when `shouldUseSupergraphMeetingDetail` is true.
 *
 * Both RTK Query hooks are always invoked (Rules of Hooks); `skip` selects the active source.
 * Supergraph responses are mapped with `mapNodeToMeeting` and merged with cache meeting intelligence when present.
 */
export const usePrimaryCalendlyMeetingDetail = ({
  shouldUseSupergraphMeetingDetail,
  shouldFetchMeetingDetailInProvider,
  eventId,
  selectedView,
  scopeType,
  scopeId,
  isCalendlyMeeting,
  detailPollingConfig,
  cacheMeetingData,
  timezone,
  hasMsTeamsFlag,
}) => {
  const shouldFetchRestApi = Boolean(shouldFetchMeetingDetailInProvider && !shouldUseSupergraphMeetingDetail)
  const shouldFetchSupergraphApi = Boolean(shouldFetchMeetingDetailInProvider && shouldUseSupergraphMeetingDetail)

  const restApiQueryArgs = useMemo(() => {
    if (!shouldFetchRestApi || !scopeType || !isCalendlyMeeting) {
      return { eventId: undefined, selectedView: undefined, scopeType: undefined, scopeId: undefined }
    }
    return { eventId, selectedView, scopeType, scopeId }
  }, [shouldFetchRestApi, eventId, scopeType, scopeId, selectedView, isCalendlyMeeting])

  const {
    data: restApiMeetingData,
    isError: restApiError,
    isSuccess: restApiSuccess,
    error: restApiErrorPayload,
  } = useGetMeetingQuery(restApiQueryArgs, {
    skip: !shouldFetchRestApi || !scopeType || !isCalendlyMeeting,
    refetchOnMountOrArgChange: true,
    ...detailPollingConfig,
  })

  const {
    data: supergraphMeetingData,
    isError: supergraphError,
    isSuccess: supergraphSuccess,
    error: supergraphErrorPayload,
  } = useGetMeetingFromSupergraphQuery({ id: eventId }, {
    skip: !shouldFetchSupergraphApi || !eventId,
    refetchOnMountOrArgChange: true,
    ...detailPollingConfig,
  })

  const primaryMeetingData = useMemo(() => {
    if (!shouldUseSupergraphMeetingDetail) {
      return restApiMeetingData
    }
    if (!supergraphMeetingData?.uuid || supergraphMeetingData.uuid !== eventId) {
      return undefined
    }
    const mapped = mapNodeToMeeting(supergraphMeetingData, timezone || 'UTC', hasMsTeamsFlag)
    const cacheMi = cacheMeetingData?.meetingIntelligence
    const detailMi = mapped.meetingIntelligence
    if (cacheMi && detailMi && typeof cacheMi === 'object' && typeof detailMi === 'object') {
      return {
        ...mapped,
        meetingIntelligence: { ...cacheMi, ...detailMi },
      }
    }
    return mapped
  }, [
    shouldUseSupergraphMeetingDetail,
    supergraphMeetingData,
    restApiMeetingData,
    cacheMeetingData,
    eventId,
    timezone,
    hasMsTeamsFlag,
  ])

  return {
    primaryMeetingData,
    primaryApiError: shouldUseSupergraphMeetingDetail ? supergraphError : restApiError,
    primaryApiSuccess: shouldUseSupergraphMeetingDetail ? supergraphSuccess : restApiSuccess,
    primaryApiErrorPayload: shouldUseSupergraphMeetingDetail ? supergraphErrorPayload : restApiErrorPayload,
  }
}
